/**
 * Two-way sync between the library and a folder of `.md` files: the decisions.
 *
 * This module only decides and applies through two small interfaces — a file
 * system and a library — so every rule below is tested against in-memory
 * versions of both, and the browser specifics live elsewhere (fsAccess.ts).
 *
 * The manifest records, per document, the file it is paired with and the
 * content both sides agreed on at the last sync. Comparing each side against
 * that common ancestor is what tells an edit from a stale copy:
 *
 *   changed here only   → copy to the other side
 *   changed on both     → a conflict: the newer version wins, the other is kept
 *                          in version history and reported
 *   gone on one side    → if the other side is unchanged, follow the deletion;
 *                          if it was edited, the edit wins and is written back
 *
 * Nothing is ever destroyed. A document deleted by the sync goes to the trash;
 * a file goes to `.markbird-trash/` inside the folder.
 */

export const TRASH_DIR = '.markbird-trash';

export interface FileEntry {
  /** Slash-separated path relative to the synced folder, e.g. `Work/Plan.md`. */
  path: string;
  lastModified: number;
}

export interface SyncFs {
  /** Every `.md` file, recursively, skipping hidden folders and the sync trash. */
  list(): Promise<FileEntry[]>;
  read(path: string): Promise<string>;
  /** Write (creating folders as needed); returns the new modification time. */
  write(path: string, text: string): Promise<number>;
  /** Move a file into the sync trash. */
  trash(path: string): Promise<void>;
  /** Delete a file outright. Only used right after its content was written elsewhere. */
  remove(path: string): Promise<void>;
}

export interface SyncDoc {
  id: string;
  title: string;
  content: string;
  folder: string | null;
  updatedAt: number;
  deleted: boolean;
}

export interface SyncLibrary {
  /** Every document, including trashed ones. */
  list(): Promise<SyncDoc[]>;
  create(doc: { title: string; content: string; folder: string | null }): Promise<string>;
  update(id: string, content: string): Promise<void>;
  trash(id: string): Promise<void>;
  setFolder(id: string, folder: string | null): Promise<void>;
  /** Keep a version that lost a conflict, restorable from history. */
  preserve(id: string, title: string, content: string): Promise<void>;
}

export interface ManifestEntry {
  path: string;
  /** Hash of the content both sides had at the last sync. */
  hash: string;
  /** The file's modification time at the last sync, to skip reading unchanged files. */
  mtime: number;
}

export type Manifest = Record<string, ManifestEntry>;

export interface SyncReport {
  toDisk: number;
  fromDisk: number;
  imported: number;
  exported: number;
  trashedDocs: number;
  trashedFiles: number;
  /** Files moved or renamed on either side. */
  moved: number;
  /** Documents where both sides had changed. */
  conflicts: { id: string; title: string }[];
}

/** FNV-1a over UTF-16 code units, with the length: cheap, and plenty to spot a change. */
export function contentHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${text.length.toString(36)}-${(h >>> 0).toString(36)}`;
}

// Characters no common file system accepts in a name, plus control characters.
// eslint-disable-next-line no-control-regex -- control characters are the point
const UNSAFE = /[\\/:*?"<>|\u0000-\u001f]/g;

/** A file name for a title: readable, safe on every OS, never empty. */
export function fileNameFor(title: string): string {
  const cleaned = title.replace(UNSAFE, '-').replace(/\s+/g, ' ').trim().replace(/^\.+/, '');
  return `${cleaned.slice(0, 120).trim() || 'Untitled'}.md`;
}

function folderOfPath(path: string): string | null {
  const slash = path.lastIndexOf('/');
  return slash > 0 ? path.slice(0, slash) : null;
}

function titleOfPath(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1).replace(/\.md$/i, '');
}

function joinPath(folder: string | null, name: string): string {
  return folder ? `${folder}/${name}` : name;
}

/** A path for a document that no other file or pairing already uses (case-insensitively). */
function freePath(doc: SyncDoc, taken: Set<string>): string {
  const name = fileNameFor(doc.title);
  const base = name.slice(0, -3);
  for (let n = 1; ; n++) {
    const candidate = joinPath(doc.folder, n === 1 ? name : `${base} ${n}.md`);
    if (!taken.has(candidate.toLowerCase())) {
      taken.add(candidate.toLowerCase());
      return candidate;
    }
  }
}

export async function reconcile(
  fs: SyncFs,
  library: SyncLibrary,
  previous: Manifest,
): Promise<{ manifest: Manifest; report: SyncReport }> {
  const report: SyncReport = {
    toDisk: 0,
    fromDisk: 0,
    imported: 0,
    exported: 0,
    trashedDocs: 0,
    trashedFiles: 0,
    moved: 0,
    conflicts: [],
  };
  const manifest: Manifest = {};
  const files = new Map((await fs.list()).map((f) => [f.path, f]));
  const docs = new Map((await library.list()).map((d) => [d.id, d]));
  // Every file some pairing still points at is spoken for before any pairing
  // goes looking for a renamed file, so two pairings can never take one file.
  const claimed = new Set(
    Object.values(previous)
      .map((e) => e.path)
      .filter((p) => files.has(p)),
  );
  const taken = new Set([...files.keys()].map((p) => p.toLowerCase()));

  const hashes = new Map<string, string>();
  const hashOf = async (path: string) => {
    let hash = hashes.get(path);
    if (hash === undefined) {
      hash = contentHash(await fs.read(path));
      hashes.set(path, hash);
    }
    return hash;
  };
  /** An unpaired file with exactly the content a pairing last agreed on: the same file, renamed or moved. */
  const findRenamed = async (hash: string): Promise<FileEntry | undefined> => {
    for (const file of files.values()) {
      if (!claimed.has(file.path) && (await hashOf(file.path)) === hash) return file;
    }
    return undefined;
  };

  const diskState = async (entry: ManifestEntry, file: FileEntry) => {
    // An unchanged modification time means an unchanged file; only a touched
    // file is read to find out whether its content really changed.
    if (file.lastModified === entry.mtime) return { changed: false, text: null as string | null };
    const text = await fs.read(file.path);
    return { changed: contentHash(text) !== entry.hash, text };
  };

  // 1. Pairs from the last sync.
  for (const [id, original] of Object.entries(previous)) {
    let entry = original;
    const doc = docs.get(id);
    const live = doc && !doc.deleted ? doc : undefined;
    let file = files.get(entry.path);
    taken.add(entry.path.toLowerCase());

    if (!file) {
      const renamed = await findRenamed(entry.hash);
      if (renamed) {
        claimed.add(renamed.path);
        file = renamed;
        entry = { ...entry, path: renamed.path, mtime: renamed.lastModified };
        if (live && folderOfPath(renamed.path) !== live.folder) {
          await library.setFolder(id, folderOfPath(renamed.path));
          live.folder = folderOfPath(renamed.path);
        }
        report.moved++;
      }
    }

    if (live && file) {
      const localChanged = contentHash(live.content) !== entry.hash;
      const { changed: diskChanged, text } = await diskState(entry, file);

      if (!localChanged && !diskChanged) {
        if (live.folder !== folderOfPath(file.path)) {
          // Moved to another folder here: the file follows.
          const path = freePath(live, taken);
          const mtime = await fs.write(path, live.content);
          await fs.remove(file.path);
          manifest[id] = { path, hash: entry.hash, mtime };
          report.moved++;
        } else {
          manifest[id] = { ...entry, mtime: file.lastModified };
        }
        continue;
      }
      if (localChanged && !diskChanged) {
        const mtime = await fs.write(file.path, live.content);
        manifest[id] = { path: file.path, hash: contentHash(live.content), mtime };
        report.toDisk++;
        continue;
      }
      const diskText = text ?? (await fs.read(file.path));
      if (!localChanged || diskText === live.content) {
        if (diskText !== live.content) {
          await library.update(id, diskText);
          report.fromDisk++;
        }
        manifest[id] = { path: file.path, hash: contentHash(diskText), mtime: file.lastModified };
        continue;
      }
      // Both changed, differently. The more recent edit wins; the other is kept.
      report.conflicts.push({ id, title: live.title });
      if (file.lastModified > live.updatedAt) {
        await library.preserve(id, live.title, live.content);
        await library.update(id, diskText);
        manifest[id] = { path: file.path, hash: contentHash(diskText), mtime: file.lastModified };
      } else {
        await library.preserve(id, live.title, diskText);
        const mtime = await fs.write(file.path, live.content);
        manifest[id] = { path: file.path, hash: contentHash(live.content), mtime };
      }
      continue;
    }

    if (live && !file) {
      if (contentHash(live.content) === entry.hash) {
        // Deleted (or moved away) on disk, untouched here: follow it.
        await library.trash(id);
        report.trashedDocs++;
      } else {
        const mtime = await fs.write(entry.path, live.content);
        manifest[id] = { path: entry.path, hash: contentHash(live.content), mtime };
        claimed.add(entry.path);
        report.toDisk++;
      }
      continue;
    }

    if (!live && file) {
      const { changed, text } = await diskState(entry, file);
      if (!changed) {
        await fs.trash(file.path);
        report.trashedFiles++;
      } else {
        // Edited on disk after it was deleted here: the edit is worth more than
        // the deletion, so it comes back as a document.
        const body = text ?? (await fs.read(file.path));
        const newId = await library.create({
          title: titleOfPath(file.path),
          content: body,
          folder: folderOfPath(file.path),
        });
        manifest[newId] = { path: file.path, hash: contentHash(body), mtime: file.lastModified };
        report.imported++;
      }
    }
    // Neither side has it any more: the pairing simply ends.
  }

  // 2. Files nobody is paired with: new on disk — unless a document with the
  //    same content is also unpaired, which is the same note arriving from both
  //    sides (a first sync with a folder that was once a backup, say).
  const unpairedDocs = [...docs.values()].filter(
    (d) => !d.deleted && !previous[d.id] && !manifest[d.id],
  );
  const byHash = new Map<string, SyncDoc[]>();
  for (const d of unpairedDocs) {
    const key = contentHash(d.content);
    byHash.set(key, [...(byHash.get(key) ?? []), d]);
  }
  for (const file of files.values()) {
    if (claimed.has(file.path)) continue;
    const text = await fs.read(file.path);
    const hash = contentHash(text);
    const twin = byHash.get(hash)?.shift();
    if (twin) {
      manifest[twin.id] = { path: file.path, hash, mtime: file.lastModified };
      continue;
    }
    const id = await library.create({
      title: titleOfPath(file.path),
      content: text,
      folder: folderOfPath(file.path),
    });
    manifest[id] = { path: file.path, hash, mtime: file.lastModified };
    report.imported++;
  }

  // 3. Documents nobody is paired with: new here.
  for (const doc of unpairedDocs) {
    if (manifest[doc.id]) continue;
    const path = freePath(doc, taken);
    const mtime = await fs.write(path, doc.content);
    manifest[doc.id] = { path, hash: contentHash(doc.content), mtime };
    report.exported++;
  }

  return { manifest, report };
}
